import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, DollarSign, Calendar, TrendingUp, ArrowRight, CheckCircle, Clock, AlertCircle, Sparkles } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardOverview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/auth/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout currentPage="/dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const { stats, orders, consultations } = dashboardData || { stats: {}, orders: [], consultations: [] };

  return (
    <DashboardLayout currentPage="/dashboard">
      {/* Welcome Section */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 data-testid="welcome-heading" className="text-2xl md:text-3xl font-bold text-white">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <Sparkles size={24} className="text-[#F97316]" />
        </div>
        <p className="text-white/50">Here's what's happening with your projects today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        {/* Total Projects */}
        <div className="bg-gradient-to-br from-[#3B82F6]/20 to-[#3B82F6]/5 border border-[#3B82F6]/30 rounded-2xl p-5 md:p-6 hover:border-[#3B82F6]/50 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#3B82F6]/20 rounded-xl flex items-center justify-center">
              <Package size={24} className="text-[#3B82F6]" />
            </div>
            <TrendingUp size={18} className="text-[#10B981]" />
          </div>
          <div className="text-3xl font-bold text-white mb-1">{stats.total_orders || 0}</div>
          <div className="text-white/50 text-sm">Total Projects</div>
        </div>

        {/* Total Invested */}
        <div className="bg-gradient-to-br from-[#8B5CF6]/20 to-[#8B5CF6]/5 border border-[#8B5CF6]/30 rounded-2xl p-5 md:p-6 hover:border-[#8B5CF6]/50 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#8B5CF6]/20 rounded-xl flex items-center justify-center">
              <DollarSign size={24} className="text-[#8B5CF6]" />
            </div>
            <TrendingUp size={18} className="text-[#10B981]" />
          </div>
          <div className="text-3xl font-bold text-white mb-1">${stats.total_spent?.toLocaleString() || 0}</div>
          <div className="text-white/50 text-sm">Total Invested</div>
        </div>

        {/* Consultations */}
        <div className="bg-gradient-to-br from-[#F97316]/20 to-[#F97316]/5 border border-[#F97316]/30 rounded-2xl p-5 md:p-6 hover:border-[#F97316]/50 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#F97316]/20 rounded-xl flex items-center justify-center">
              <Calendar size={24} className="text-[#F97316]" />
            </div>
            <Clock size={18} className="text-[#F59E0B]" />
          </div>
          <div className="text-3xl font-bold text-white mb-1">{stats.total_consultations || 0}</div>
          <div className="text-white/50 text-sm">Consultations</div>
        </div>

        {/* Completed */}
        <div className="bg-gradient-to-br from-[#10B981]/20 to-[#10B981]/5 border border-[#10B981]/30 rounded-2xl p-5 md:p-6 hover:border-[#10B981]/50 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
              <CheckCircle size={24} className="text-[#10B981]" />
            </div>
            <span className="text-[#10B981] text-sm font-semibold">100%</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {orders.filter(o => o.status === 'completed').length}
          </div>
          <div className="text-white/50 text-sm">Completed</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
        {/* Active Projects */}
        <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg md:text-xl font-bold text-white">Active Projects</h2>
            <button
              data-testid="view-all-projects"
              onClick={() => navigate('/dashboard/projects')}
              className="text-[#3B82F6] hover:text-[#60A5FA] transition-colors text-sm flex items-center gap-2 font-medium"
            >
              View All
              <ArrowRight size={16} />
            </button>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package size={32} className="text-white/20" />
              </div>
              <p className="text-white/50 mb-4">No projects yet</p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 font-semibold text-white rounded-xl transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
              >
                Browse Services
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.slice(0, 3).map((order) => (
                <div
                  key={order.id}
                  data-testid={`project-${order.id}`}
                  className="bg-[#0A0A0F] border border-white/10 rounded-xl p-4 hover:border-[#3B82F6]/30 transition-all cursor-pointer group"
                  onClick={() => navigate(`/dashboard/projects/${order.id}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-white font-semibold mb-1 group-hover:text-[#3B82F6] transition-colors">{order.order_number}</div>
                      <div className="text-white/40 text-sm">
                        {order.items?.length || 0} service{order.items?.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      order.status === 'completed' ? 'bg-[#10B981]/20 text-[#10B981]' :
                      order.status === 'processing' ? 'bg-[#3B82F6]/20 text-[#3B82F6]' :
                      'bg-[#F97316]/20 text-[#F97316]'
                    }`}>
                      {order.status}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-white/40 mb-1">
                      <span>Progress</span>
                      <span>
                        {order.status === 'completed' ? '100' :
                         order.status === 'processing' ? '50' : '25'}%
                      </span>
                    </div>
                    <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${order.status === 'completed' ? '100' :
                                  order.status === 'processing' ? '50' : '25'}%`,
                          background: 'linear-gradient(90deg, #3B82F6, #8B5CF6)'
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="text-white/60 text-sm font-medium">
                    ${order.total?.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6">
          <h2 className="text-lg md:text-xl font-bold text-white mb-6">Recent Activity</h2>

          <div className="space-y-4">
            {consultations.length === 0 && orders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Clock size={32} className="text-white/20" />
                </div>
                <p className="text-white/50">No recent activity</p>
              </div>
            ) : (
              <>
                {consultations.slice(0, 2).map((consultation) => (
                  <div key={consultation.id} className="flex gap-4 pb-4 border-b border-white/10">
                    <div className="w-10 h-10 bg-[#F97316]/10 rounded-xl flex items-center justify-center border border-[#F97316]/30">
                      <Calendar size={18} className="text-[#F97316]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium mb-1">Consultation Scheduled</div>
                      <div className="text-white/50 text-sm">{consultation.service}</div>
                      <div className="text-white/30 text-xs mt-1">
                        {new Date(consultation.date).toLocaleDateString()} at {consultation.time}
                      </div>
                    </div>
                  </div>
                ))}

                {orders.slice(0, 2).map((order) => (
                  <div key={order.id} className="flex gap-4 pb-4 border-b border-white/10 last:border-0">
                    <div className="w-10 h-10 bg-[#3B82F6]/10 rounded-xl flex items-center justify-center border border-[#3B82F6]/30">
                      <Package size={18} className="text-[#3B82F6]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium mb-1">Order Placed</div>
                      <div className="text-white/50 text-sm">{order.order_number}</div>
                      <div className="text-white/30 text-xs mt-1">
                        {new Date(order.timestamp).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardOverview;
